import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import creditActionApi from "@/api/creditAction";
import creditTransactionApi, {
    CreateTransactionRequest,
} from "@/api/creditTransaction";
import { TransactionContext } from "@/context/TransactionContext";
import { sendReferralSuccessEmail } from "@/email/send";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { queryKeys } from "@/hooks/useAppData";
import { openModal } from "@/store/modal/slice";
import { getIsCreditsEnabled } from "@/store/tenant/selectors";
import { getUserCreditBalance, getUserId } from "@/store/user/selectors";
import { updateUserDetails } from "@/store/user/slice";
import { ICreditAction } from "@/types/creditAction";
import { PerformActionParams } from "@/types/creditTransaction";
import { ModalTypes } from "@/types/modal";
import { getIsLivePreviewPage } from "@/utils/routes";
import { formatCreditAmount } from "@/utils/string";

const THIRTY_MINUTES = 30 * 60 * 1000;
const ONE_HOUR = 60 * 60 * 1000;

export const TransactionProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const dispatch = useAppDispatch();
    const currentUserId = useAppSelector(getUserId);
    const [isUpdatingBalance, setIsUpdatingBalance] = useState(false);
    const creditBalance = useAppSelector(getUserCreditBalance);
    const isCreditsEnabled = useAppSelector(getIsCreditsEnabled);
    const isLivePreviewPage = getIsLivePreviewPage();

    const { data: creditActions = [] } = useQuery({
        queryKey: queryKeys.creditActions,
        queryFn: async () => {
            if (isLivePreviewPage) {
                return [];
            }
            const response = await creditActionApi.getCreditActionsApi({
                limit: 999,
            });
            return response.results;
        },
        staleTime: THIRTY_MINUTES,
        gcTime: ONE_HOUR,
    });

    const getCreditActions = (filter: {
        actionCode?: string;
        type?: string;
    }) => {
        let actions = creditActions.filter(action => action.isActive);

        if (filter.actionCode) {
            actions = actions.filter(
                action => action.code === filter.actionCode,
            );
        }

        if (filter.type) {
            actions = actions.filter(action => action.type === filter.type);
        }

        return actions;
    };

    const getCreditActionByCode = (actionCode: string) => {
        return creditActions.find(
            action => action.code === actionCode && action.isActive,
        );
    };

    const getCanAffordCreditAction = (actionCode: string) => {
        if (!isCreditsEnabled) {
            return true;
        }
        const action = getCreditActionByCode(actionCode);
        if (!action) {
            return false;
        }
        const positiveCreditValue = Math.abs(action.credits ?? 0);
        return creditBalance - positiveCreditValue >= 0;
    };

    const getCreditActionCostByCode = (actionCode: string) => {
        const action = getCreditActionByCode(actionCode);
        const credits = Number(action?.credits);
        return isNaN(credits) ? 0 : Math.abs(credits);
    };

    const getAndFormatCreditActionCostByCode = (actionCode: string) => {
        const credits = getCreditActionCostByCode(actionCode);
        if (credits === 0) {
            return ""; // Should avoid showing 0 credits
        }
        return formatCreditAmount(credits);
    };

    const handleInsufficientFunds = ({
        transactionAmount,
        availableBalance,
        actionCode,
    }: {
        transactionAmount: number;
        availableBalance: number;
        actionCode: string;
    }) => {
        dispatch(
            openModal({
                modalType: ModalTypes.INSUFFICIENT_FUNDS,
                props: { transactionAmount, availableBalance, actionCode },
            }),
        );
    };

    const getTransactionAmount = (action: ICreditAction, amount?: number) => {
        // Variable amount actions require an amount
        if (action.amountType === "VARIABLE") {
            if (amount === undefined) {
                throw new Error(
                    "Amount is required for variable credit actions",
                );
            }
            return amount;
        }

        // Fixed amount actions use the predefined credit amount
        return action.credits || 0;
    };

    const validateAndAddTransaction = async (params: PerformActionParams) => {
        const { creditActionError, hasInsufficientFunds } =
            validateCreditAction(params);

        if (creditActionError || hasInsufficientFunds) {
            throw new Error(creditActionError || "Insufficient funds");
        }

        await addTransaction(params);
    };

    const validateCreditAction = ({
        actionCode,
        amount,
        userId = currentUserId,
    }: PerformActionParams) => {
        // Convert single action parameters to arrays for consistent processing
        const actionCodes = Array.isArray(actionCode)
            ? actionCode
            : [actionCode];
        const amounts = Array.isArray(amount) ? amount : [amount];

        if (!userId) {
            return {
                creditActionError: "userId not found",
                hasInsufficientFunds: false,
            };
        }

        let totalTransactionAmount = 0;

        // Validate each action
        for (let i = 0; i < actionCodes.length; i++) {
            const currentActionCode = actionCodes[i];
            const currentAmount = amounts[i];

            const action = getCreditActionByCode(currentActionCode);
            if (!action) {
                return {
                    creditActionError: `Action not found for code: ${currentActionCode}`,
                    hasInsufficientFunds: false,
                };
            }

            if (
                action.amountType === "VARIABLE" &&
                currentAmount === undefined
            ) {
                return {
                    creditActionError: `Amount is required for variable credit action: ${currentActionCode}`,
                    hasInsufficientFunds: false,
                };
            }

            const transactionAmount = getTransactionAmount(
                action,
                currentAmount,
            );
            totalTransactionAmount += transactionAmount;
        }

        // Check if total transaction amount would result in negative balance
        if (totalTransactionAmount < 0 && isCreditsEnabled) {
            const newBalance = creditBalance + totalTransactionAmount; // Amounts are defined as negative values

            if (newBalance < 0) {
                handleInsufficientFunds({
                    transactionAmount: totalTransactionAmount,
                    availableBalance: creditBalance,
                    actionCode: actionCodes[0], // Pass first action code for error modal
                });
                return {
                    creditActionError: "Insufficient credits",
                    hasInsufficientFunds: true,
                };
            }
        }

        return {
            creditActionError: null,
            hasInsufficientFunds: false,
        };
    };

    const addTransaction = async ({
        actionCode,
        amount,
        metadata,
        description,
        createdBy,
        userId = currentUserId,
    }: PerformActionParams) => {
        try {
            const action = getCreditActionByCode(actionCode);
            if (!action || !userId) {
                throw new Error("Action or userId not found");
            }

            setIsUpdatingBalance(true);

            const transactionMetadata = {
                ...metadata,
            };

            const transactionAmount = getTransactionAmount(action, amount);

            const newTransaction: CreateTransactionRequest = {
                actionCode,
                amount: transactionAmount,
                description: description || action.description || "",
                metadata: transactionMetadata,
                userId,
                createdBy: createdBy || userId,
                type: action.type,
            };

            const { userUpdates, emailDetails } =
                await creditTransactionApi.createTransaction(newTransaction);

            if (currentUserId === userId) {
                dispatch(updateUserDetails(userUpdates));
            }

            if (emailDetails && emailDetails.emailType === "referral-success") {
                await sendReferralSuccessEmail(emailDetails);
            }
        } catch (error) {
            console.error("Error adding transaction", error);
        } finally {
            setIsUpdatingBalance(false);
        }
    };

    return (
        <TransactionContext.Provider
            value={{
                addTransaction,
                validateCreditAction,
                validateAndAddTransaction,
                getCreditActionCostByCode,
                getAndFormatCreditActionCostByCode,
                isUpdatingBalance,
                getCanAffordCreditAction,
                getCreditActions,
                getCreditActionByCode,
            }}
        >
            {children}
        </TransactionContext.Provider>
    );
};
